import React, { useState } from 'react';
import { ExternalLink, Code2, Brain, FileText, Calendar, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Projects = () => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');

  const rawProjects = t('projects.items', { returnObjects: true });
  const projectItems = Array.isArray(rawProjects) ? rawProjects : [];

  const icons = [Brain, FileText, Calendar, Code2, Brain, Brain, Award];

  const techStack = [
    ['ONNX Runtime', 'Python', 'FastAPI', 'PyTorch'],
    ['Python', 'NLP', 'LangChain', 'LlamaIndex'],
    ['Python', 'FastAPI', 'Airtable API', 'Automation'],
    ['PyTorch', 'FastAPI', 'CNN', 'REST API'],
    ['Python', 'Scikit-learn', 'ROC-AUC', 'Machine Learning'],
    ['Python', 'TensorFlow.js', 'Computer Vision', 'CNN'],
    ['Python', 'NLP', 'Scikit-learn', 'Hyperparameter Tuning']
  ];

  const projectLinks = [
    '/blog_document_classifier.html',
    '/pdf_reader.html',
    '/llm_reservaciones.html',
    '/ecommerce.html',
    '/detector_fraudes.html',
    '/blog_pet_classifier.html',
    '/competencia_ML.html'
  ];

  const projectCategories = [
    'ml-cv',
    'genai-automation',
    'genai-automation',
    'ml-cv',
    'ml-cv',
    'ml-cv',
    'ml-cv'
  ];

  const filterButtons = [
    { id: 'all', label: t('projects.filters.all', 'Todos') },
    { id: 'ml-cv', label: t('projects.filters.mlCv', 'Machine Learning & CV') },
    { id: 'genai-automation', label: t('projects.filters.genai', 'GenAI & Automatización') }
  ];

  const fullProjects = projectItems.map((item, index) => ({
    ...item,
    icon: icons[index] || Code2,
    year: item.year || '2026',
    techs: item.techs || techStack[index] || [],
    highlights: item.highlights || item.bulletPoints || [],
    link: item.link || projectLinks[index] || '#',
    category: projectCategories[index] || 'ml-cv',
  }));

  const filteredProjects = activeFilter === 'all'
    ? fullProjects
    : fullProjects.filter(p => p.category === activeFilter);

  return (
    <section id="projects" className="min-h-screen bg-background text-foreground transition-colors duration-300 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Encabezado */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
          <div>
            <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-2">
              {t('projects.title', 'Proyectos Destacados')}
            </h2>
            <div className="w-16 h-1 bg-blue-600 rounded"></div>
          </div>

          {/* Filtros */}
          <div className="flex items-center gap-1 bg-slate-200/60 dark:bg-slate-800/80 p-1.5 rounded-xl self-start md:self-auto border border-transparent dark:border-slate-700">
            {filterButtons.map((btn) => (
              <button
                key={btn.id}
                onClick={() => setActiveFilter(btn.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeFilter === btn.id
                    ? 'bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400 shadow-sm font-semibold'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grilla de Tarjetas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, idx) => {
            const IconComponent = project.icon;
            return (
              <div 
                key={idx}
                className="bg-white dark:bg-slate-900/90 rounded-2xl border border-slate-100 dark:border-slate-800 p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div>
                  {/* Ícono + Año */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <span className="text-slate-400 dark:text-slate-500 text-sm font-normal">
                      {project.year}
                    </span>
                  </div>

                  {/* Título */}
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 leading-snug">
                    {project.title}
                  </h3>

                  {/* Descripción */}
                  <p className="text-slate-600 dark:text-slate-300 text-sm mb-4 leading-relaxed">
                    {project.description}
                  </p>

                  {/* Highlights / Puntos clave */}
                  {project.highlights && project.highlights.length > 0 && (
                    <ul className="space-y-2 mb-6">
                      {project.highlights.map((point, pIdx) => (
                        <li key={pIdx} className="text-xs text-slate-600 dark:text-slate-300 flex items-start gap-2 leading-normal">
                          <span className="text-blue-600 dark:text-blue-400 font-bold text-sm leading-none">•</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  {/* Badges de Tecnologías */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.techs.map((tech, tIdx) => (
                      <span
                        key={tIdx}
                        className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs rounded-md font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Botón Ver Proyecto */}
                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-2.5 px-4 rounded-xl border border-blue-500 dark:border-blue-600 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/50 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ver Proyecto
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default Projects;